import { useGetMetricGroupsDropdownQuery } from '@api/initiatives';
import { getFrameworksOptions, getStandardsOptions } from '@constants/standards-frameworks';
import { PACK, SURVEY } from '@constants/terminology';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { ScopeGroups, SurveyScope } from '@g17eco/types/surveyCommon';
import { getCustomGroupOptions } from '@utils/metricGroup';
import { useMemo } from 'react';
import { getGroup, type Group } from '@g17eco/core';
import { getGroupChildrenTags } from '@utils/groups';
import type { MetricGroupDropdownItem } from '@g17eco/types/metricGroup';

interface Props {
  initiativeId: string;
  modules: string[];
  onChange: (value: string[]) => void;
  scope?: SurveyScope;
}

const standardsOptions = getStandardsOptions();
const frameworksOptions = getFrameworksOptions();

const isPartialScope = (group: Group | MetricGroupDropdownItem | undefined, scopeCodes: string[]) => {
  if (!group) {
    return false;
  }
  const children = getGroupChildrenTags(group);
  return children.some((code) => scopeCodes.includes(code));
};

// TODO: Move this component to @app/common when all related components are moved onto @app
export const ModuleDropdown = ({ initiativeId, onChange, modules, scope }: Props) => {
  const { data: metricGroups } = useGetMetricGroupsDropdownQuery(initiativeId);

  const options = useMemo(() => {
    const customGroupOptions = getCustomGroupOptions(metricGroups ?? []);
    if (!scope) {
      return [...customGroupOptions, ...standardsOptions, ...frameworksOptions];
    }

    const standardCodes = scope[ScopeGroups.Standards];
    const frameworkCodes = scope[ScopeGroups.Frameworks];
    const customGroupIds = scope[ScopeGroups.Custom];

    return [
      ...customGroupOptions.filter((op) => {
        if (customGroupIds.includes(op.value)) {
          return true;
        }
        const group = metricGroups?.find((g) => g._id === op.value);
        return isPartialScope(group, customGroupIds);
      }),
      ...standardsOptions.filter((op) => {
        if (standardCodes.includes(op.value)) {
          return true;
        }
        const group = getGroup('standards', op.value);
        return isPartialScope(group, standardCodes);
      }),
      ...frameworksOptions.filter((op) => {
        // We are dealing options of subgroups here, so those must be explicitly added
        if (frameworkCodes.includes(op.value)) {
          return true;
        }
        const group = getGroup(op.scopeType, op.frameworkCode);
        // Root framework is added, so all subgroups are included
        return group ? frameworkCodes.includes(group.code) : false ;
      })
    ]
  }, [metricGroups, scope])

  return (
    <SelectFactory
      selectType={SelectTypes.MultipleSelect}
      placeholder={`Select ${SURVEY.ADJECTIVE} ${PACK.SINGULAR}`}
      options={options}
      onChange={onChange}
      values={modules}
      className='w-100'
    />
  );
};
